import React, { useState } from 'react';
import { apiService } from '../services/api';

// Hidden pharmacy webhook smoke driver — fires Beluga's verbatim ask
// (visitType=testVisitType, pharmacyId=271328, medId=testVisitType)
// through our backend so we can validate the full E2E surface end to end
// (FE → /beluga/create-visit-v2 → Beluga → /beluga-webhook receivers).
//
// Route: /test-pharmacy
// NOT linked from anywhere — known URL only. Safe in prod because:
//   - Backend will reject testVisitType unless it's in VALID_VISIT_TYPES
//     (BE needs to flip a whitelist before this works — see
//     BACKEND_PHARMACY_WEBHOOKS_PRESMOKE_PRD §6).
//   - paymentId is a sentinel "PHARMACY_SMOKE_NO_PAYMENT" — backend needs
//     to accept this OR allow a missing paymentId for testVisitType
//     submissions (also documented in the presmoke PRD).
//
// This component intentionally has zero questionnaire UI. Beluga's
// instruction was "any questionnaire / no questionnaire" so we send a
// minimal payload with all PURPLE named fields populated + an empty Q/A
// array. Backend mapAnswersToFormObj should handle it.

const PHARMACY_SMOKE_VALUES = {
  visitType: 'testVisitType',
  pharmacyId: '271328',
  medId: 'testVisitType',
  medName: 'Test Medication',
  strength: '1mg',
};

const PharmacySmokeStep = () => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'firing' | 'success' | 'error'
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  const fire = async () => {
    setStatus('firing');
    setResponse(null);
    setError(null);

    // Unique per click so backend doesn't reject "Duplicate masterId".
    const ts = Date.now();
    const smokeEmail = `smoke-pharmacy-${ts}@fitpal-test.com`;

    // Minimum-viable contact + shipping. Backend's address-merge logic
    // requires a non-empty state/zip even for testVisitType (per the
    // existing AntiAging / metabolic mapper).
    const contact = {
      first_name: 'Pharmacy',
      last_name: 'SmokeTest',
      email: smokeEmail,
      phone: '5555550199',
      dob: '01/01/1985',
      address: '100 Test Street',
      address2: '',
      city: 'Charleston',
      state: 'WV',
      zip: '25301',
    };

    // No questionnaire — Beluga said "any questionnaire / no questionnaire".
    // We still send PURPLE named-field placeholders so the mapper doesn't
    // null-deref on extraction.
    const answers = [
      { slide_id: 'sex-at-birth', question: 'sex', answer: 'Male' },
      { slide_id: 'medical-conditions', question: 'medical-conditions', answer: 'None' },
      { slide_id: 'self-reported-meds', question: 'self-reported-meds', answer: 'None' },
      { slide_id: 'allergies', question: 'allergies', answer: 'NKDA' },
    ];

    try {
      const result = await apiService.submitForm(
        answers,
        contact,
        'PHARMACY_SMOKE_NO_PAYMENT',  // sentinel; backend may need to allowlist
        'testVisitType',
        PHARMACY_SMOKE_VALUES.visitType,
        {
          pharmacyId: PHARMACY_SMOKE_VALUES.pharmacyId,
          patientPreference: [
            {
              name: PHARMACY_SMOKE_VALUES.medName,
              strength: PHARMACY_SMOKE_VALUES.strength,
              quantity: 1,
              refills: 0,
              medId: PHARMACY_SMOKE_VALUES.medId,
            },
          ],
          smokeMode: true,  // explicit hint; backend may use to bypass payment gate
        },
      );
      setStatus('success');
      setResponse(result);
    } catch (err) {
      setStatus('error');
      setError({
        message: err.message,
        status: err.response?.status,
        body: err.response?.data,
      });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="w-full py-4 px-4 border-b">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl font-semibold text-gray-900">
            Pharmacy Webhook Smoke (testVisitType / pharmacyId 271328)
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Hidden route. Fires the Beluga staging visit creation that's been
            in their court since 2026-05-25. Bypasses Square.
          </p>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        {/* Payload preview */}
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
            Outgoing payload
          </h2>
          <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto">
{JSON.stringify(
  {
    flow: 'testVisitType',
    visitType: 'testVisitType',
    pharmacyId: '271328',
    paymentId: 'PHARMACY_SMOKE_NO_PAYMENT',
    smokeMode: true,
    patientPreference: [
      { name: 'Test Medication', strength: '1mg', quantity: 1, refills: 0, medId: 'testVisitType' },
    ],
  },
  null,
  2,
)}
          </pre>
        </section>

        {/* Fire button */}
        <section className="mb-8">
          <button
            onClick={fire}
            disabled={status === 'firing'}
            className="btn-primary"
            data-testid="pharmacy-smoke-fire-btn"
          >
            {status === 'firing' ? 'Firing…' : 'Fire pharmacy smoke'}
          </button>
        </section>

        {/* Result */}
        {status === 'success' && response && (
          <section className="mb-8" data-testid="pharmacy-smoke-result-success">
            <h2 className="text-sm font-semibold text-green-700 uppercase tracking-wide mb-3">
              ✓ Backend accepted
            </h2>
            {response.masterId && (
              <div className="bg-green-50 border border-green-300 rounded-xl p-4 mb-3">
                <div className="text-xs text-green-700 uppercase tracking-wide mb-1">
                  masterId (share with Beluga in Slack)
                </div>
                <div
                  className="text-2xl font-mono font-bold text-green-900 select-all"
                  data-testid="pharmacy-smoke-masterid"
                >
                  {response.masterId}
                </div>
              </div>
            )}
            <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto">
{JSON.stringify(response, null, 2)}
            </pre>
          </section>
        )}

        {status === 'error' && error && (
          <section className="mb-8" data-testid="pharmacy-smoke-result-error">
            <h2 className="text-sm font-semibold text-red-700 uppercase tracking-wide mb-3">
              ✗ Backend rejected (this is data — read it)
            </h2>
            <div className="bg-red-50 border border-red-300 rounded-xl p-4 mb-3">
              <div className="text-xs text-red-700 uppercase tracking-wide mb-1">
                HTTP status
              </div>
              <div className="text-xl font-mono text-red-900">
                {error.status ?? 'no response'}
              </div>
            </div>
            <pre className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs overflow-x-auto">
{JSON.stringify(error, null, 2)}
            </pre>

            {/* Diagnostic hints */}
            <div className="mt-4 text-sm text-gray-600 space-y-2">
              {error.body?.belugaError === 'Company does not have that visit type' && (
                <p>
                  → Beluga ops needs to add{' '}
                  <code className="bg-gray-100 px-1 rounded">testVisitType</code> to
                  the <code>fitpal</code> company allowlist (same kind of fix as the
                  open <code>trt</code> allowlist thread).
                </p>
              )}
              {error.body?.msg?.includes('payment') && (
                <p>
                  → Backend rejected the payment sentinel. BE needs to allowlist{' '}
                  <code className="bg-gray-100 px-1 rounded">testVisitType</code> for
                  payment bypass (see presmoke PRD §2).
                </p>
              )}
              {error.body?.msg?.includes('visit type') &&
                !error.body?.belugaError && (
                  <p>
                    → Our backend rejected{' '}
                    <code className="bg-gray-100 px-1 rounded">testVisitType</code>{' '}
                    before reaching Beluga. Add it to{' '}
                    <code>VALID_VISIT_TYPES</code> in the
                    <code>/beluga/create-visit-v2</code> handler.
                  </p>
                )}
            </div>
          </section>
        )}

        <footer className="text-xs text-gray-400 border-t pt-4">
          docs/BACKEND_PHARMACY_WEBHOOKS_PRESMOKE_PRD.md · Beluga thread 2026-05-25
        </footer>
      </main>
    </div>
  );
};

export default PharmacySmokeStep;
